import { type PlateEditor, focusEditor } from "@udecode/plate";
import { useRef } from "react";

/**
 * Radix's DropdownMenu moves DOM focus around as it closes (even with the default
 * auto-focus-to-trigger behaviour prevented). Refocusing the editor immediately in the
 * item's onSelect handler races that and can lose keystrokes typed right after picking
 * an item (e.g. "/" right after "Turn into > Condition"). onCloseAutoFocus fires exactly
 * once, synchronously, once Radix is actually done closing, so restore focus there instead
 * of guessing at a delay with requestAnimationFrame/setTimeout.
 */
export const useDeferredEditorFocus = (editor: PlateEditor | undefined) => {
  const pendingSelectionRef = useRef<PlateEditor["selection"]>(null);

  const scheduleFocus = (selection: PlateEditor["selection"]) => {
    pendingSelectionRef.current = selection ?? null;
  };

  const onCloseAutoFocus = (event: Event) => {
    event.preventDefault();
    if (editor && pendingSelectionRef.current) {
      focusEditor(editor, pendingSelectionRef.current);
    }
    pendingSelectionRef.current = null;
  };

  return { scheduleFocus, onCloseAutoFocus };
};
